// Thin typed fetch client. Cookies (session) are sent automatically because
// the dev server proxies /api as first-party.

export interface ApiError {
  error: string;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    headers: body ? { "content-type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
    credentials: "include",
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) {
    throw new Error((data as ApiError).error ?? `Request failed (${res.status})`);
  }
  return data as T;
}

export const api = {
  get: <T>(p: string) => request<T>("GET", p),
  post: <T>(p: string, body?: unknown) => request<T>("POST", p, body),
  del: <T>(p: string) => request<T>("DELETE", p),
};

export const waitlist = {
  count: () => request<{ count: number }>("GET", "/waitlist/count"),
  join: (email: string, gameInterest?: string) =>
    request<{ ok: boolean; count: number }>("POST", "/waitlist", { email, gameInterest, source: "landing" }),
};

export const contact = {
  send: (email: string, subject: string, message: string) =>
    request<{ ok: boolean }>("POST", "/contact", { email, subject, message }),
};

// ----- Domain types (mirror the API responses) -----

export interface Me {
  id: string;
  email: string;
  name: string | null;
  role: string;
  organizationId: string;
}

export interface CatalogPlan {
  slug: string;
  name: string;
  ramMb: number;
  diskGb: number;
  backupSlots: number;
  priceCents: number;
  recommendedPlayers: string;
}

export interface CatalogGame {
  slug: string;
  name: string;
  manifest: unknown;
  plans: CatalogPlan[];
}

export interface Catalog {
  games: CatalogGame[];
  regions: { slug: string; name: string }[];
}

export interface ServerView {
  id: string;
  name: string;
  slug: string;
  status: string;
  hostname: string | null;
  primaryPort: number | null;
  connectionAddress: string | null;
  lastError: string | null;
  subscriptionId: string | null;
  game: { slug: string; name: string };
  plan: { slug: string; name: string; ramMb: number; diskGb: number; priceCents: number };
  region: { slug: string; name: string };
  createdAt: string;
}

export interface ServerEvent {
  id: string;
  type: string;
  message: string;
  createdAt: string;
}

export interface Backup {
  id: string;
  status: string;
  sizeBytes: number | null;
  reason: string;
  expiresAt: string | null;
  createdAt: string;
}
